// ─── ProtocolsPage helpers ────────────────────────────────────────────────────

import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Shield, ShieldCheck, AlertCircle, ChevronDown, CheckCircle2, ExternalLink,
  RefreshCw, Zap, DollarSign, Bitcoin, Coins, Globe, Download, FileJson,
  Plug, Terminal,
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { AnimatedSection } from '@/components/ui/AnimatedSection';
import { ErrorBanner } from '@/components/shared/ErrorBanner';
import { SkeletonCard } from '@/components/shared/SkeletonCard';
import { cn } from '@/lib/utils';
import type { ProtocolHealthItem, PaymentRailHealthItem, RailStatus, SettlementRail } from '@/types/app';
import type { BadgeVariant } from '@/types/components.types';

const RAIL_STATUS_VARIANT: Record<RailStatus, BadgeVariant> = {
  active:   'success',
  ready:    'info',
  preview:  'warning',
  degraded: 'danger',
};

const RAIL_ICON: Record<SettlementRail, LucideIcon> = {
  stripe:          DollarSign,
  btc_lightning:   Bitcoin,
  usdt_lightning:  Coins,
  usdt_onchain:    Coins,
  xrpl_rlusd:      Globe,
  algorand_x402:   Zap,          // v3.0.0: Payment Layer
};

const ProtocolStatusBadge = ({ status }: { status: string }) => {
  const variant =
    status === 'Healthy'  ? 'success' :
    status === 'Active'   ? 'success' :
    status === 'Preview'  ? 'info'    :
    status === 'Degraded' ? 'danger'  : 'warning';
  return <Badge variant={variant}>{status}</Badge>;
};

// ─── ProtocolsPage ────────────────────────────────────────────────────────────
// Sections (in order):
//   1. Loading skeleton
//   2. Error banner
//   3. System Health Banner
//   4. Routing Protocol Surfaces  (2-col expandable grid · A2A / ACP / UCP / NLIP)
//   5. Payment Layer Protocol     (x402 dedicated card · emerald · elintra.algo)
//   6. Settlement Rails           (3-col grid · Algorand expanded with full attestation metadata)
//   7. Developer Toolkit          (RESTORED from V3.1 · Protocol Schema / Webhook / Auth)

export const ProtocolsPage = ({
  protocolHealth,
  paymentRails,
  isLoading,
  error,
  onClearError,
}: {
  protocolHealth: ProtocolHealthItem[];
  paymentRails:   PaymentRailHealthItem[];
  isLoading:      boolean;
  error:          string | null;
  onClearError?:  () => void;
}) => {
  const [expandedProto, setExpandedProto] = useState<string | null>(null);

  const routingProtocols    = protocolHealth.filter((p) => p.type !== 'PAYMENT_LAYER');
  const x402Protocol        = protocolHealth.find((p)  => p.type === 'PAYMENT_LAYER');
  const algoRail            = paymentRails.find((r)    => r.id   === 'algorand_x402');

  const healthyRoutingCount = routingProtocols.filter((p) => p.status === 'Healthy').length;
  const activeRailsCount    = paymentRails.filter((r) => r.status === 'active').length;
  const coreHealthy         =
    protocolHealth.find((p) => p.id === 'a2a')?.status  === 'Healthy' &&
    protocolHealth.find((p) => p.id === 'acp')?.status  === 'Healthy';
  const x402Status          = x402Protocol?.status ?? 'Unknown';

  // ── Loading state ───────────────────────────────────────────────────────────
  if (isLoading && protocolHealth.length === 0) {
    return (
      <div className="max-w-6xl space-y-4">
        <SkeletonCard rows={2} />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {Array.from({ length: 4 }, (_, i) => <SkeletonCard key={i} rows={3} />)}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl space-y-6">

      {/* ── 1. Error Banner ──────────────────────────────────────────────────── */}
      <AnimatePresence>
        {error && (
          <ErrorBanner
            message={error}
            onDismiss={onClearError}
          />
        )}
      </AnimatePresence>

      {/* ── 2. System Health Banner ──────────────────────────────────────────── */}
      <Card className={cn(
        'p-5 flex items-center gap-4',
        coreHealthy
          ? 'border-emerald-500/30 bg-emerald-500/5'
          : 'border-amber-500/30  bg-amber-500/5',
      )}>
        <div className={cn(
          'w-10 h-10 rounded-full flex items-center justify-center shrink-0',
          coreHealthy ? 'bg-emerald-500/10' : 'bg-amber-500/10',
        )}>
          {coreHealthy
            ? <ShieldCheck className="w-5 h-5 text-emerald-400" />
            : <AlertCircle className="w-5 h-5 text-amber-400" />}
        </div>

        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm">
            {coreHealthy ? 'All Core Protocols Operational' : 'Core Protocol Warning'}
          </p>
          <p className="text-xs text-zinc-400 mt-0.5">
            {healthyRoutingCount} of {routingProtocols.length} routing protocols healthy
            {' · '}
            {activeRailsCount} of {paymentRails.length} payment rails active
            {' · '}
            x402{' '}
            <span className={cn(
              'font-mono font-semibold',
              x402Status === 'Active' ? 'text-emerald-400' : 'text-amber-400',
            )}>
              {x402Status}
            </span>
          </p>
        </div>

        <Badge variant={coreHealthy ? 'success' : 'warning'}>
          {coreHealthy ? 'All Systems Go' : 'Degraded'}
        </Badge>
      </Card>

      {/* ── 3. Routing Protocol Surfaces ─────────────────────────────────────── */}
      <div>
        <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
          Routing Protocol Surfaces
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {routingProtocols.map((proto, i) => {
            const isExpanded = expandedProto === proto.id;
            const iconColor  =
              proto.status === 'Healthy'  ? 'text-emerald-400' :
              proto.status === 'Preview'  ? 'text-sky-400'     : 'text-red-400';
            const iconBg     =
              proto.status === 'Healthy'  ? 'bg-emerald-500/10 border-emerald-500/20' :
              proto.status === 'Preview'  ? 'bg-sky-500/10     border-sky-500/20'     :
                                            'bg-red-500/10     border-red-500/20';
            return (
              <motion.div
                key={proto.id}
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.06 }}
              >
                <Card className={cn(
                  'transition-all duration-200',
                  isExpanded && 'border-zinc-700',
                )}>
                  {/* Row: click target */}
                  <button
                    type="button"
                    className="w-full flex items-center gap-4 px-5 py-4
                               hover:bg-zinc-800/20 transition-colors text-left cursor-pointer"
                    onClick={() => setExpandedProto(isExpanded ? null : proto.id)}
                  >
                    <div className={cn(
                      'w-10 h-10 rounded-lg flex items-center justify-center shrink-0 border',
                      iconBg,
                    )}>
                      <Shield className={cn('w-5 h-5', iconColor)} />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                        <span className="font-bold">{proto.label}</span>
                        <ProtocolStatusBadge status={proto.status} />
                        <Badge variant="default" className="text-[10px] px-1.5 py-0">
                          PROTOCOL
                        </Badge>
                      </div>
                      <p className="text-xs text-zinc-500 font-mono truncate">
                        {proto.endpoint}
                      </p>
                    </div>

                    <div className="flex items-center gap-2 shrink-0">
                      {proto.latency && (
                        <span className="text-xs font-mono text-zinc-500 hidden md:block">
                          {proto.latency}
                        </span>
                      )}
                      <ChevronDown className={cn(
                        'w-4 h-4 text-zinc-500 transition-transform duration-200',
                        isExpanded && 'rotate-180',
                      )} />
                    </div>
                  </button>

                  {/* Expandable detail panel */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2, ease: 'easeOut' }}
                        className="overflow-hidden"
                      >
                        <div className="px-5 pt-3 pb-4 border-t border-zinc-800/60 space-y-3">
                          <div className="grid grid-cols-2 gap-3">
                            <div>
                              <p className="text-xs text-zinc-600 mb-0.5">Owner</p>
                              <p className="text-xs text-zinc-300 font-medium leading-tight">
                                {proto.owner}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-600 mb-0.5">Endpoint</p>
                              <p className="text-xs font-mono text-zinc-400 truncate">
                                {proto.endpoint}
                              </p>
                            </div>
                            {proto.version && (
                              <div>
                                <p className="text-xs text-zinc-600 mb-0.5">Version</p>
                                <p className="text-xs font-mono text-violet-400">
                                  {proto.version}
                                </p>
                              </div>
                            )}
                            {proto.latency && (
                              <div>
                                <p className="text-xs text-zinc-600 mb-0.5">Latency</p>
                                <p className="text-xs font-mono text-emerald-400">
                                  {proto.latency}
                                </p>
                              </div>
                            )}
                          </div>

                          <div className="flex items-center gap-2">
                            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
                            <span className="text-xs text-zinc-400">
                              Endpoint verified · serving agent discovery requests
                            </span>
                          </div>

                          <div className="flex items-center gap-2 pt-0.5">
                            <Button variant="outline" size="sm">
                              <ExternalLink className="w-3.5 h-3.5 mr-1.5" />
                              View Spec
                            </Button>
                            <Button variant="outline" size="sm">
                              <RefreshCw className="w-3.5 h-3.5 mr-1.5" />
                              Re-check
                            </Button>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </Card>
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* ── 4. Payment Layer Protocol — x402 ─────────────────────────────────── */}
      {x402Protocol && (
        <AnimatedSection delay={0.1}>
          <div>
            <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
              Payment Layer Protocol
            </p>
            <Card className="border-emerald-500/20 bg-emerald-500/[0.03]">
              {/* Header row */}
              <div className="flex items-center gap-4 px-5 py-4 border-b border-zinc-800/60">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 border border-emerald-500/20
                                flex items-center justify-center shrink-0">
                  <Zap className="w-5 h-5 text-emerald-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-0.5 flex-wrap">
                    <span className="font-bold">{x402Protocol.label}</span>
                    <ProtocolStatusBadge status={x402Protocol.status} />
                    <Badge variant="success" className="gap-1 text-[10px] px-1.5 py-0">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" />
                      PAYMENT LAYER
                    </Badge>
                  </div>
                  <p className="text-xs text-zinc-500 font-mono truncate">
                    {x402Protocol.endpoint}
                  </p>
                </div>
              </div>

              {/* Metadata grid */}
              <div className="px-5 py-4 grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Owner</p>
                  <p className="text-xs text-zinc-300 font-medium leading-tight">
                    {x402Protocol.owner}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Settlement Address</p>
                  <p className="text-xs font-mono text-emerald-400 font-semibold">
                    {algoRail?.address ?? 'elintra.algo'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Facilitator</p>
                  <p className="text-xs font-mono text-zinc-300">
                    {algoRail?.facilitator ?? 'GoPlausible'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Finality</p>
                  <p className="text-xs font-mono text-emerald-400 font-semibold">
                    {algoRail?.finalityMs
                      ? `${(algoRail.finalityMs / 1000).toFixed(0)}s FINAL`
                      : '4s FINAL'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Network</p>
                  <p className="text-xs font-mono text-zinc-400">
                    {algoRail?.network ?? 'algorand-mainnet'}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-zinc-600 mb-1">Endpoint</p>
                  <p className="text-xs font-mono text-zinc-400 truncate">
                    {x402Protocol.endpoint}
                  </p>
                </div>
                <div className="md:col-span-2 flex items-end">
                  <a
                    href="https://algoexplorer.io/address/elintra.algo"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-1.5 text-xs text-zinc-500
                               hover:text-zinc-300 transition-colors"
                  >
                    <ExternalLink className="w-3 h-3" />
                    algoexplorer.io/address/elintra.algo
                  </a>
                </div>
              </div>
            </Card>
          </div>
        </AnimatedSection>
      )}

      {/* ── 5. Settlement Rails ───────────────────────────────────────────────── */}
      <AnimatedSection delay={0.15}>
        <div>
          <p className="text-xs font-semibold text-zinc-500 uppercase tracking-wider mb-3">
            Settlement Rails
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {paymentRails.map((rail) => {
              const Icon    = RAIL_ICON[rail.id] ?? DollarSign;
              const isAlgo  = rail.id === 'algorand_x402';
              const variant = RAIL_STATUS_VARIANT[rail.status] ?? 'default';

              return (
                <Card
                  key={rail.id}
                  className={cn(
                    'p-4',
                    isAlgo && 'border-emerald-500/20 bg-emerald-500/[0.03]',
                  )}
                >
                  {/* Rail header */}
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2.5 min-w-0">
                      <div className={cn(
                        'w-8 h-8 rounded-lg flex items-center justify-center shrink-0',
                        isAlgo
                          ? 'bg-emerald-500/10 border border-emerald-500/20'
                          : 'bg-zinc-800      border border-zinc-700',
                      )}>
                        <Icon className={cn(
                          'w-4 h-4',
                          isAlgo ? 'text-emerald-400' : 'text-zinc-400',
                        )} />
                      </div>
                      <div className="min-w-0">
                        <p className="text-xs font-semibold text-zinc-200 leading-tight truncate">
                          {rail.label}
                        </p>
                        <p className="text-[10px] text-zinc-600 font-mono mt-0.5">
                          {rail.phase}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center gap-1.5 shrink-0 ml-2">
                      {isAlgo && (
                        <Badge variant="success" className="gap-1 text-[10px] px-1.5 py-0">
                          <span className="w-1 h-1 rounded-full bg-emerald-400 animate-pulse" />
                          LIVE
                        </Badge>
                      )}
                      <Badge variant={variant} className="capitalize text-[10px] px-1.5 py-0">
                        {rail.status}
                      </Badge>
                    </div>
                  </div>

                  {/* Algorand: expanded attestation metadata */}
                  {isAlgo ? (
                    <div className="space-y-1.5 pt-2 border-t border-zinc-800/60">
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0">
                          Address
                        </span>
                        <span className="text-xs font-mono text-emerald-400 font-semibold truncate">
                          {rail.address}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0">
                          Facilitator
                        </span>
                        <span className="text-xs font-mono text-zinc-300">
                          {rail.facilitator}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0">
                          Finality
                        </span>
                        <span className="text-xs font-mono text-emerald-400 font-semibold">
                          {rail.finalityMs
                            ? `${(rail.finalityMs / 1000).toFixed(0)}s FINAL`
                            : '4s FINAL'}
                        </span>
                      </div>
                      <div className="flex items-center justify-between gap-2">
                        <span className="text-[10px] text-zinc-600 uppercase tracking-wider shrink-0">
                          Network
                        </span>
                        <span className="text-xs font-mono text-zinc-400">
                          {rail.network}
                        </span>
                      </div>
                      <div className="pt-1">
                        <a
                          href="https://algoexplorer.io/address/elintra.algo"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-[10px] text-zinc-600
                                     hover:text-zinc-400 transition-colors"
                        >
                          <ExternalLink className="w-2.5 h-2.5 shrink-0" />
                          algoexplorer.io/address/elintra.algo
                        </a>
                      </div>
                    </div>
                  ) : (
                    /* Non-Algorand rails: show address if present */
                    rail.address && (
                      <div className="pt-2 border-t border-zinc-800/60">
                        <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-1">
                          Address
                        </p>
                        <p className="text-xs font-mono text-zinc-400 truncate">
                          {rail.address}
                        </p>
                      </div>
                    )
                  )}
                </Card>
              );
            })}
          </div>
        </div>
      </AnimatedSection>

      {/* ── 6. Developer Toolkit (RESTORED from V3.1) ────────────────────────── */}
      <AnimatedSection delay={0.2}>
        <Card className="p-5">
          <div className="flex items-center gap-3 mb-5">
            <div className="w-8 h-8 rounded-lg bg-violet-500/10 border border-violet-500/20
                            flex items-center justify-center shrink-0">
              <Terminal className="w-4 h-4 text-violet-400" />
            </div>
            <div>
              <h3 className="font-semibold text-sm">Developer Toolkit</h3>
              <p className="text-xs text-zinc-500 mt-0.5">
                Integration resources for protocol surfaces and payment rails
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            {/* Protocol Schema */}
            <button
              type="button"
              className={cn(
                'flex flex-col items-start gap-3 p-4 rounded-lg text-left',
                'border border-zinc-800 bg-zinc-900/40',
                'hover:bg-zinc-800/60 hover:border-zinc-700',
                'transition-all duration-200 cursor-pointer',
              )}
            >
              <div className="w-8 h-8 rounded-md bg-zinc-800 border border-zinc-700
                              flex items-center justify-center">
                <Download className="w-4 h-4 text-zinc-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-zinc-200">Protocol Schema</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">
                  Download OpenAPI spec · JSON
                </p>
              </div>
            </button>

            {/* Webhook Config */}
            <button
              type="button"
              className={cn(
                'flex flex-col items-start gap-3 p-4 rounded-lg text-left',
                'border border-zinc-800 bg-zinc-900/40',
                'hover:bg-zinc-800/60 hover:border-zinc-700',
                'transition-all duration-200 cursor-pointer',
              )}
            >
              <div className="w-8 h-8 rounded-md bg-zinc-800 border border-zinc-700
                              flex items-center justify-center">
                <FileJson className="w-4 h-4 text-zinc-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-zinc-200">Webhook Config</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">
                  Configure event push endpoints
                </p>
              </div>
            </button>

            {/* Auth Credentials */}
            <button
              type="button"
              className={cn(
                'flex flex-col items-start gap-3 p-4 rounded-lg text-left',
                'border border-zinc-800 bg-zinc-900/40',
                'hover:bg-zinc-800/60 hover:border-zinc-700',
                'transition-all duration-200 cursor-pointer',
              )}
            >
              <div className="w-8 h-8 rounded-md bg-zinc-800 border border-zinc-700
                              flex items-center justify-center">
                <Plug className="w-4 h-4 text-zinc-400" />
              </div>
              <div>
                <p className="text-xs font-semibold text-zinc-200">Auth Credentials</p>
                <p className="text-[10px] text-zinc-500 mt-0.5 leading-relaxed">
                  View and rotate API keys
                </p>
              </div>
            </button>
          </div>
        </Card>
      </AnimatedSection>

    </div>
  );
};
